import { addDays, diffDays, type IsoDate } from './dates';
import type { AcademicLevel } from './enums';
import { type StepTemplateData, type StepTemplateInput, type TemplateStep, stepTemplateInputSchema } from './schemas/step-templates';
import type { StepRequirementInput } from './schemas/steps';

export interface PlatformStepTemplate extends StepTemplateData {
  /** Chave estável: o modelo é criado no banco com ela e a superadministração pode editá-lo */
  seedKey: string;
}

const PDF = ['pdf'];
/** Formatos aceitos no envio de arquivos (regra `upload.allowedExtensions`). */
const DOC = ['pdf', 'docx', 'odt'];
const SHEET = ['xlsx', 'csv'];
const IMG_PDF = ['pdf', 'png', 'jpg', 'jpeg'];
/** Atividades simples servem a qualquer tipo de trabalho de pesquisa. */
const ALL_RESEARCH = ['PESQUISA', 'TCC', 'MONOGRAFIA', 'DISSERTACAO', 'TESE', 'POS_DOUTORADO', 'ARTIGO', 'ARTIGO_CONFERENCIA'];
/** Modelo base de mestrado e doutorado, sugerido no convite do(a) orientando(a). */
export const BASIC_TEMPLATE_SEED_KEY = 'plataforma-basico';
/** Modelo base da iniciação científica (um ciclo de bolsa), sugerido no convite de bolsistas de IC. */
export const IC_BASIC_TEMPLATE_SEED_KEY = 'plataforma-basico-iniciacao-cientifica';

/** Opções do item "Fase atual da pesquisa", em ordem cronológica (ADR-0023). */
export const RESEARCH_PHASES = [
  '1. Planejamento / Revisão bibliográfica',
  '2. Coleta de dados / Trabalho de campo / Experimentos',
  '3. Análise e processamento de dados',
  '4. Redação do texto',
  '5. Aguardando exame de qualificação / Avaliação',
  '6. Ajustes pós-qualificação / Revisão',
  '7. Em revisão final pelo orientador',
  '8. Pronto para defesa / Submissão final',
];
/** Item de lista que o(a) orientando(a) atualiza conforme a pesquisa avança. */
export const RESEARCH_PHASE_ITEM: StepRequirementInput = {
  kind: 'CHOICE',
  label: 'Fase atual da pesquisa',
  description: 'Atualize sempre que a pesquisa mudar de fase.',
  options: RESEARCH_PHASES,
};

/**
 * Modelos fornecidos pela plataforma. Os de dissertação, tese e artigo seguem a lógica do "Guia prático para
 * redação científica" (Gilson Volpato): pergunta e conclusão orientam o texto; escreve-se a partir dos
 * resultados (figuras e tabelas), depois conclusão e título, discussão, método, introdução e resumo.
 */
const PLATFORM: (StepTemplateInput & { id: string })[] = [
  {
    id: BASIC_TEMPLATE_SEED_KEY,
    name: 'Básico Mestrado e Doutorado',
    description:
      'Plano base de um curso de mestrado ou doutorado: revisão sistemática, disciplinas, artigo principal, texto de qualificação, segundo artigo e definição científica da pesquisa / dissertação / tese. Escolhido no convite, os prazos se distribuem do ingresso ao prazo do título.',
    modalityKeys: ['DISSERTACAO', 'TESE'],
    steps: [
      {
        title: 'Revisão sistemática',
        description: 'Levantamento sistemático da literatura da área: pergunta, bases, busca, triagem e síntese. É a base do artigo principal e do texto de qualificação.',
        durationDays: 150,
        requirements: [
          RESEARCH_PHASE_ITEM,
          { kind: 'TEXT', label: 'Pergunta de pesquisa e critérios de busca' },
          { kind: 'NUMBER', label: 'Artigos incluídos na síntese', unit: 'artigos' },
          { kind: 'FILES', label: 'Síntese da revisão', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Disciplinas',
        description: 'Créditos exigidos pelo programa concluídos.',
        durationDays: 120,
        requirements: [
          { kind: 'TEXT', label: 'Disciplinas cursadas e conceitos' },
          { kind: 'FILES', label: 'Histórico escolar', minCount: 1, extensions: IMG_PDF, required: false },
        ],
      },
      {
        title: 'Artigo principal',
        description: 'Primeiro artigo da pesquisa, submetido a periódico ou evento.',
        durationDays: 60,
        requirements: [
          RESEARCH_PHASE_ITEM,
          { kind: 'TEXT', label: 'Periódico ou evento e situação da submissão' },
          { kind: 'FILES', label: 'Manuscrito submetido', minCount: 1, extensions: DOC },
          { kind: 'LINK', label: 'Comprovante de submissão', required: false },
        ],
      },
      {
        title: 'Texto de qualificação',
        description: 'Texto entregue à banca de qualificação: revisão, método e resultados parciais.',
        durationDays: 35,
        requirements: [
          RESEARCH_PHASE_ITEM,
          { kind: 'FILES', label: 'Texto de qualificação', minCount: 1, extensions: PDF },
          { kind: 'FILES', label: 'Slides da apresentação', minCount: 1, extensions: ['pdf', 'pptx'], required: false },
        ],
      },
      {
        title: 'Segundo artigo',
        description: 'Segundo artigo, com os resultados obtidos depois da qualificação.',
        durationDays: 185,
        requirements: [
          RESEARCH_PHASE_ITEM,
          { kind: 'FILES', label: 'Manuscrito submetido', minCount: 1, extensions: DOC },
          { kind: 'LINK', label: 'Comprovante de submissão', required: false },
        ],
      },
      {
        title: 'Definição científica da pesquisa / Dissertação / Tese',
        description:
          'Tema, pergunta e método da pesquisa, atualizados ao longo do curso, e a versão final para a defesa (dissertação no mestrado, tese no doutorado), com o artigo principal submetido.',
        durationDays: 180,
        requirements: [
          { kind: 'TEXT', label: 'Tema e pergunta de pesquisa', description: 'Descrição clara do problema de investigação e dos objetivos centrais.' },
          { kind: 'TEXT', label: 'Metodologia e fonte de dados', description: 'Abordagem metodológica (qualitativa, quantitativa, experimental) e origem dos dados.' },
          RESEARCH_PHASE_ITEM,
          { kind: 'FILES', label: 'Versão para a banca', description: 'Arquivo completo final destinado à banca examinadora.', minCount: 1, extensions: PDF },
          { kind: 'FILES', label: 'Comprovante de submissão do artigo', minCount: 1, extensions: IMG_PDF },
        ],
      },
    ],
  },
  {
    id: IC_BASIC_TEMPLATE_SEED_KEY,
    name: 'Básico Iniciação científica',
    description:
      'Plano base de um ciclo de iniciação científica (PIBIC, PIBITI e similares): plano de trabalho, leituras orientadas, relatório parcial, coleta e análise dos dados, relatório final e apresentação no seminário de IC. Escolhido no convite, os prazos se distribuem do ingresso ao fim da vigência.',
    modalityKeys: ['PESQUISA'],
    steps: [
      {
        title: 'Plano de trabalho',
        description: 'Pergunta, objetivos e cronograma do projeto do(a) bolsista, combinados com a orientação.',
        durationDays: 30,
        requirements: [
          { kind: 'TEXT', label: 'Pergunta e objetivos do projeto' },
          { kind: 'FILES', label: 'Plano de trabalho com cronograma', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Leituras orientadas',
        description: 'Leitura dos textos indicados pela orientação, com um fichamento de cada um.',
        durationDays: 60,
        requirements: [
          { kind: 'NUMBER', label: 'Textos lidos', unit: 'textos' },
          { kind: 'FILES', label: 'Fichamentos', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Relatório parcial',
        description: 'Relatório de meio de vigência, como pedem os editais de IC: o que foi feito, resultados preliminares e próximos passos.',
        durationDays: 90,
        requirements: [
          RESEARCH_PHASE_ITEM,
          { kind: 'FILES', label: 'Relatório parcial', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Coleta e análise dos dados',
        description: 'Dados coletados e organizados, com as figuras e tabelas dos resultados.',
        durationDays: 105,
        requirements: [
          RESEARCH_PHASE_ITEM,
          { kind: 'FILES', label: 'Dados organizados', minCount: 1, extensions: [...SHEET, 'zip', 'txt'] },
          { kind: 'FILES', label: 'Figuras e tabelas', minCount: 1, extensions: [...DOC, 'png', 'jpg', 'jpeg'] },
          { kind: 'TEXT', label: 'Principal resultado' },
        ],
      },
      {
        title: 'Relatório final',
        description: 'Relatório final da vigência, no formato pedido pelo programa de IC.',
        durationDays: 45,
        requirements: [{ kind: 'FILES', label: 'Relatório final', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Apresentação no seminário de IC',
        description: 'Resumo submetido e apresentação (pôster ou oral) no seminário ou congresso de iniciação científica.',
        durationDays: 35,
        requirements: [
          { kind: 'FILES', label: 'Resumo submetido', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Pôster ou slides', minCount: 1, extensions: ['pdf', 'pptx'] },
          { kind: 'TEXT', label: 'Evento e data', required: false },
        ],
      },
    ],
  },
  {
    id: 'plataforma-revisao-sistematica',
    name: 'Revisão sistemática (PRISMA)',
    description: 'Do protocolo ao manuscrito, seguindo as etapas do PRISMA 2020: busca, triagem, elegibilidade, extração, síntese e redação.',
    modalityKeys: ['DISSERTACAO', 'TESE', 'ARTIGO', 'ARTIGO_CONFERENCIA', 'PESQUISA', 'TCC', 'MONOGRAFIA'],
    steps: [
      {
        title: 'Protocolo da revisão',
        description: 'Defina a pergunta de pesquisa, os critérios de inclusão e exclusão, as bases e a estratégia de busca.',
        durationDays: 14,
        requirements: [
          { kind: 'TEXT', label: 'Pergunta de pesquisa (PICO/PICo)' },
          { kind: 'TEXT', label: 'Critérios de inclusão e exclusão' },
          { kind: 'TEXT', label: 'Bases de dados e strings de busca' },
          { kind: 'FILES', label: 'Protocolo completo', minCount: 1, extensions: DOC },
          { kind: 'LINK', label: 'Registro do protocolo (PROSPERO, OSF)', required: false },
        ],
      },
      {
        title: 'Busca nas bases',
        description: 'Execute as buscas e exporte os resultados de cada base.',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'Exportações das bases (BibTeX, CSV ou TXT/RIS)', minCount: 1, extensions: ['bib', 'csv', 'txt'] },
          { kind: 'NUMBER', label: 'Total de registros encontrados', unit: 'registros' },
          { kind: 'TEXT', label: 'Data da busca e strings usadas em cada base' },
        ],
      },
      {
        title: 'Duplicatas e triagem por título e resumo',
        description: 'Remova as duplicatas e faça a triagem por título e resumo, de preferência com dois revisores.',
        durationDays: 21,
        requirements: [
          { kind: 'NUMBER', label: 'Registros após a remoção de duplicatas', unit: 'registros' },
          { kind: 'NUMBER', label: 'Selecionados para leitura na íntegra', unit: 'artigos' },
          { kind: 'LINK', label: 'Planilha ou projeto da triagem (Rayyan, planilha)', required: false },
        ],
      },
      {
        title: 'Leitura na íntegra (elegibilidade)',
        description: 'Leia os textos completos e aplique os critérios de elegibilidade.',
        durationDays: 21,
        requirements: [
          { kind: 'FILES', label: 'Artigos incluídos (PDF)', minCount: 1, extensions: PDF },
          { kind: 'NUMBER', label: 'Total de estudos incluídos', unit: 'estudos' },
          { kind: 'TEXT', label: 'Motivos de exclusão, com quantidades' },
        ],
      },
      {
        title: 'Extração de dados e avaliação da qualidade',
        description: 'Preencha a planilha de extração e avalie o risco de viés ou a qualidade dos estudos.',
        durationDays: 21,
        requirements: [
          { kind: 'FILES', label: 'Planilha de extração', minCount: 1, extensions: SHEET },
          { kind: 'TEXT', label: 'Instrumento de avaliação de qualidade usado e resumo' },
        ],
      },
      {
        title: 'Síntese e fluxograma PRISMA',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'Fluxograma PRISMA', minCount: 1, extensions: IMG_PDF },
          { kind: 'TEXT', label: 'Síntese dos principais achados' },
        ],
      },
      {
        title: 'Redação do manuscrito',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Manuscrito completo', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Checklist PRISMA preenchido', minCount: 1, extensions: DOC, required: false },
        ],
      },
    ],
  },
  {
    id: 'plataforma-revisao-literatura',
    name: 'Revisão de literatura',
    description: 'Levantamento, leitura e síntese da literatura sobre o tema: base para o referencial teórico.',
    modalityKeys: ['TCC', 'MONOGRAFIA', 'DISSERTACAO', 'TESE', 'PESQUISA', 'POS_DOUTORADO'],
    steps: [
      {
        title: 'Levantamento bibliográfico',
        description: 'Busque e selecione os textos mais relevantes sobre o tema.',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'Artigos selecionados (PDF)', minCount: 10, extensions: PDF },
          { kind: 'TEXT', label: 'Bases consultadas e palavras-chave' },
          { kind: 'NUMBER', label: 'Total de artigos triados', unit: 'artigos', required: false },
        ],
      },
      {
        title: 'Fichamento',
        durationDays: 21,
        requirements: [{ kind: 'FILES', label: 'Fichamentos dos textos', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Síntese escrita (referencial teórico)',
        durationDays: 21,
        requirements: [{ kind: 'FILES', label: 'Texto do referencial teórico', minCount: 1, extensions: DOC }],
      },
    ],
  },
  {
    id: 'plataforma-artigo',
    name: 'Artigo científico: da revista à submissão',
    description:
      'Segue a lógica do "Guia prático para redação científica" (Volpato): escolher a revista, construir os resultados (figuras e tabelas), definir a conclusão e o título que a expressa e só então escrever discussão, método, introdução e resumo.',
    modalityKeys: ['ARTIGO', 'ARTIGO_CONFERENCIA', 'DISSERTACAO', 'TESE', 'POS_DOUTORADO', 'PESQUISA'],
    steps: [
      {
        title: 'Escolha da revista ou conferência',
        description: 'Escreva para a revista escolhida: leia artigos recentes dela para conhecer o leitor, o formato e a profundidade esperados.',
        durationDays: 7,
        requirements: [
          { kind: 'TEXT', label: 'Revista ou conferência escolhida e por quê (escopo, leitores, fator de impacto/Qualis)' },
          { kind: 'LINK', label: 'Normas para autores' },
          { kind: 'FILES', label: 'Artigos recentes da revista usados como referência (PDF)', minCount: 3, extensions: PDF },
        ],
      },
      {
        title: 'Resultados: figuras e tabelas',
        description: 'Comece pelos resultados: monte as figuras e tabelas que sustentam a mensagem do artigo, cada uma com legenda completa.',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'Figuras e tabelas com legendas', minCount: 1, extensions: [...DOC, 'png', 'jpg', 'jpeg', 'xlsx', 'pptx'] },
          { kind: 'TEXT', label: 'O que cada figura/tabela mostra (uma frase por item)' },
        ],
      },
      {
        title: 'Conclusão e título',
        description: 'A conclusão responde à pergunta do estudo em poucas frases; o título deve comunicar essa conclusão.',
        durationDays: 7,
        requirements: [
          { kind: 'TEXT', label: 'Conclusão (resposta direta à pergunta do estudo)' },
          { kind: 'TEXT', label: 'Título que expresse a conclusão' },
        ],
      },
      {
        title: 'Discussão',
        description: 'Defenda a conclusão: compare com a literatura, explique o que os resultados significam e reconheça as limitações.',
        durationDays: 14,
        requirements: [{ kind: 'FILES', label: 'Texto da discussão', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Método',
        description: 'Descreva o suficiente para outra pessoa repetir o estudo e avaliar a validade dos resultados.',
        durationDays: 7,
        requirements: [{ kind: 'FILES', label: 'Texto do método', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Introdução',
        description: 'Do contexto à lacuna e à pergunta/objetivo: a introdução prepara o leitor exatamente para a conclusão do artigo.',
        durationDays: 7,
        requirements: [
          { kind: 'FILES', label: 'Texto da introdução', minCount: 1, extensions: DOC },
          { kind: 'TEXT', label: 'Objetivo ou hipótese (última frase da introdução)' },
        ],
      },
      {
        title: 'Resumo, palavras-chave e versão completa',
        description: 'O resumo, escrito por último, traz objetivo, método, principais resultados e a conclusão.',
        durationDays: 7,
        requirements: [
          { kind: 'TEXT', label: 'Resumo' },
          { kind: 'FILES', label: 'Versão completa do artigo', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Revisão da orientação e formatação',
        description: 'Incorpore as revisões e formate conforme as normas da revista (referências, limites de palavras, carta ao editor).',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'Versão final formatada', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Carta ao editor (cover letter)', minCount: 1, extensions: DOC, required: false },
        ],
      },
      {
        title: 'Submissão',
        durationDays: 7,
        requirements: [
          { kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF },
          { kind: 'TEXT', label: 'Revista ou conferência e data da submissão' },
        ],
      },
    ],
  },
  {
    id: 'plataforma-projeto-pesquisa',
    name: 'Projeto de pesquisa',
    description: 'Do problema à versão final do projeto (qualificação, seleção ou edital).',
    modalityKeys: ['DISSERTACAO', 'TESE', 'TCC', 'MONOGRAFIA', 'PESQUISA', 'POS_DOUTORADO'],
    steps: [
      {
        title: 'Problema, objetivos e justificativa',
        durationDays: 14,
        requirements: [
          { kind: 'TEXT', label: 'Problema de pesquisa' },
          { kind: 'TEXT', label: 'Objetivo geral e objetivos específicos' },
          { kind: 'TEXT', label: 'Justificativa' },
        ],
      },
      {
        title: 'Referencial teórico',
        durationDays: 21,
        requirements: [{ kind: 'FILES', label: 'Texto do referencial', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Metodologia',
        durationDays: 21,
        requirements: [{ kind: 'FILES', label: 'Texto da metodologia', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Cronograma e versão final do projeto',
        durationDays: 14,
        requirements: [{ kind: 'FILES', label: 'Projeto completo (PDF)', minCount: 1, extensions: PDF }],
      },
    ],
  },
  {
    id: 'plataforma-comite-etica',
    name: 'Comitê de ética (Plataforma Brasil)',
    description: 'Preparação dos documentos, submissão e acompanhamento do parecer do CEP.',
    modalityKeys: ['DISSERTACAO', 'TESE', 'TCC', 'PESQUISA', 'POS_DOUTORADO'],
    steps: [
      {
        title: 'Documentos para o CEP',
        description: 'TCLE, instrumentos de coleta, folha de rosto e projeto no formato do CEP.',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'TCLE / TALE', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Instrumentos de coleta (questionários, roteiros)', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Folha de rosto assinada', minCount: 1, extensions: PDF },
        ],
      },
      {
        title: 'Submissão na Plataforma Brasil',
        durationDays: 7,
        requirements: [
          { kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF },
          { kind: 'TEXT', label: 'Número do CAAE' },
        ],
      },
      {
        title: 'Parecer do CEP',
        description: 'Responda às pendências, se houver, e anexe o parecer de aprovação.',
        durationDays: 45,
        requirements: [{ kind: 'FILES', label: 'Parecer consubstanciado de aprovação', minCount: 1, extensions: PDF }],
      },
    ],
  },
  {
    id: 'plataforma-tcc',
    name: 'TCC: da proposta à apresentação',
    description: 'Proposta, referencial, metodologia, desenvolvimento, versão final e apresentação.',
    modalityKeys: ['TCC', 'MONOGRAFIA'],
    steps: [
      {
        title: 'Proposta',
        durationDays: 14,
        requirements: [
          { kind: 'TEXT', label: 'Tema, problema e objetivo' },
          { kind: 'FILES', label: 'Proposta escrita', minCount: 1, extensions: DOC, required: false },
        ],
      },
      { title: 'Referencial teórico', durationDays: 30, requirements: [{ kind: 'FILES', label: 'Capítulo do referencial', minCount: 1, extensions: DOC }] },
      { title: 'Metodologia', durationDays: 21, requirements: [{ kind: 'FILES', label: 'Capítulo da metodologia', minCount: 1, extensions: DOC }] },
      { title: 'Desenvolvimento e resultados', durationDays: 45, requirements: [{ kind: 'FILES', label: 'Capítulos de desenvolvimento e resultados', minCount: 1, extensions: DOC }] },
      { title: 'Versão final', durationDays: 21, requirements: [{ kind: 'FILES', label: 'Versão final (PDF)', minCount: 1, extensions: PDF }] },
      {
        title: 'Apresentação',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'Slides da apresentação', minCount: 1, extensions: ['pdf', 'pptx'] },
          { kind: 'TEXT', label: 'Data e banca', required: false },
        ],
      },
    ],
  },
  {
    id: 'plataforma-dissertacao',
    name: 'Dissertação de mestrado',
    description:
      'Do problema à defesa, com a lógica da redação científica (Volpato): pergunta e conclusão esperada primeiro; na escrita, resultados → conclusão → título → discussão → método → introdução → resumo. Qualificação e defesa entram automaticamente.',
    modalityKeys: ['DISSERTACAO'],
    steps: [
      {
        title: 'Pergunta, hipótese e contribuição',
        description: 'Defina a pergunta de pesquisa, a hipótese (ou a conclusão que se espera poder afirmar) e por que ela importa.',
        durationDays: 21,
        requirements: [
          { kind: 'TEXT', label: 'Pergunta de pesquisa' },
          { kind: 'TEXT', label: 'Hipótese ou conclusão esperada' },
          { kind: 'TEXT', label: 'Lacuna e contribuição (por que responder essa pergunta)' },
        ],
      },
      {
        title: 'Revisão da literatura (estado da arte)',
        durationDays: 45,
        requirements: [
          { kind: 'FILES', label: 'Artigos centrais do tema (PDF)', minCount: 20, extensions: PDF },
          { kind: 'FILES', label: 'Síntese do estado da arte', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Projeto e delineamento do método',
        description: 'Delineamento, população/amostra, variáveis, instrumentos e plano de análise.',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Projeto de pesquisa', minCount: 1, extensions: DOC },
          { kind: 'TEXT', label: 'Necessita de comitê de ética? Situação', required: false },
        ],
      },
      {
        title: 'Coleta de dados',
        durationDays: 120,
        requirements: [
          { kind: 'FILES', label: 'Dados coletados (planilha ou base)', minCount: 1, extensions: [...SHEET, 'zip', 'json', 'txt'] },
          { kind: 'NUMBER', label: 'Tamanho da amostra obtida', unit: 'casos' },
          { kind: 'TEXT', label: 'Desvios em relação ao planejado', required: false },
        ],
      },
      {
        title: 'Análise: figuras e tabelas dos resultados',
        description: 'Os resultados vêm antes do texto: figuras e tabelas que respondem à pergunta.',
        durationDays: 45,
        requirements: [
          { kind: 'FILES', label: 'Figuras e tabelas com legendas', minCount: 1, extensions: [...DOC, 'png', 'jpg', 'jpeg', 'xlsx'] },
          { kind: 'FILES', label: 'Scripts ou relatório da análise', minCount: 1, extensions: ['py', 'r', 'ipynb', 'pdf', 'docx', 'zip'], required: false },
        ],
      },
      {
        title: 'Conclusão e título',
        description: 'A conclusão responde à pergunta; o título comunica a conclusão.',
        durationDays: 7,
        requirements: [
          { kind: 'TEXT', label: 'Conclusão em poucas frases' },
          { kind: 'TEXT', label: 'Título que expresse a conclusão' },
        ],
      },
      {
        title: 'Discussão e método escritos',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Capítulo/seção de discussão', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Capítulo/seção de método', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Introdução e resumo',
        description: 'Introdução do contexto à lacuna e ao objetivo; resumo escrito por último.',
        durationDays: 21,
        requirements: [
          { kind: 'FILES', label: 'Introdução', minCount: 1, extensions: DOC },
          { kind: 'TEXT', label: 'Resumo' },
        ],
      },
      {
        title: 'Versão completa para a banca',
        durationDays: 21,
        requirements: [{ kind: 'FILES', label: 'Dissertação completa (PDF)', minCount: 1, extensions: PDF }],
      },
      {
        title: 'Artigo derivado submetido',
        description: 'Requisito para a defesa: artigo com os resultados da dissertação submetido a periódico ou conferência.',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Artigo submetido (PDF)', minCount: 1, extensions: PDF },
          { kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF },
        ],
      },
    ],
  },
  {
    id: 'plataforma-tese-tradicional',
    name: 'Tese de doutorado (tradicional, em capítulos)',
    description:
      'Tese em capítulos (introdução, referencial, método, resultados, discussão e conclusão), com a lógica da redação científica (Volpato): a tese central orienta tudo e a escrita parte dos resultados. Qualificação e defesa entram automaticamente.',
    modalityKeys: ['TESE'],
    steps: [
      {
        title: 'Tese central, problema e hipóteses',
        description: 'Qual afirmação original a tese defende? Defina o problema, as hipóteses e a contribuição em relação à literatura.',
        durationDays: 30,
        requirements: [
          { kind: 'TEXT', label: 'Tese central (a afirmação a ser defendida)' },
          { kind: 'TEXT', label: 'Problema e hipóteses' },
          { kind: 'TEXT', label: 'Contribuição original' },
        ],
      },
      {
        title: 'Referencial teórico (capítulo)',
        durationDays: 120,
        requirements: [
          { kind: 'FILES', label: 'Artigos e obras centrais (PDF)', minCount: 30, extensions: PDF },
          { kind: 'FILES', label: 'Capítulo do referencial teórico', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Método (capítulo) e projeto de tese',
        description: 'Delineamento, participantes/corpus, instrumentos, procedimentos e plano de análise.',
        durationDays: 60,
        requirements: [
          { kind: 'FILES', label: 'Capítulo de método', minCount: 1, extensions: DOC },
          { kind: 'TEXT', label: 'Comitê de ética: situação', required: false },
        ],
      },
      {
        title: 'Coleta de dados',
        durationDays: 240,
        requirements: [
          { kind: 'FILES', label: 'Dados coletados (planilha, base ou transcrições)', minCount: 1, extensions: [...SHEET, 'zip', 'json', 'txt', 'docx'] },
          { kind: 'NUMBER', label: 'Tamanho da amostra/corpus obtido', unit: 'casos' },
          { kind: 'TEXT', label: 'Desvios em relação ao planejado', required: false },
        ],
      },
      {
        title: 'Análise: figuras e tabelas dos resultados',
        durationDays: 90,
        requirements: [
          { kind: 'FILES', label: 'Figuras e tabelas com legendas', minCount: 1, extensions: [...DOC, 'png', 'jpg', 'jpeg', 'xlsx'] },
          { kind: 'FILES', label: 'Scripts ou relatório da análise', minCount: 1, extensions: ['py', 'r', 'ipynb', 'pdf', 'docx', 'zip'], required: false },
        ],
      },
      {
        title: 'Resultados (capítulo)',
        durationDays: 60,
        requirements: [{ kind: 'FILES', label: 'Capítulo de resultados', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Conclusões da tese',
        description: 'O que os resultados permitem afirmar sobre a tese central — em poucas frases, antes de escrever a discussão.',
        durationDays: 14,
        requirements: [
          { kind: 'TEXT', label: 'Conclusões (respostas diretas ao problema e às hipóteses)' },
          { kind: 'TEXT', label: 'Título que expresse a tese' },
        ],
      },
      {
        title: 'Discussão (capítulo)',
        durationDays: 60,
        requirements: [{ kind: 'FILES', label: 'Capítulo de discussão', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Introdução, conclusão e resumo',
        description: 'Introdução do contexto à lacuna e à tese; capítulo de conclusão; resumo e abstract por último.',
        durationDays: 45,
        requirements: [
          { kind: 'FILES', label: 'Introdução', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Capítulo de conclusão', minCount: 1, extensions: DOC },
          { kind: 'TEXT', label: 'Resumo' },
        ],
      },
      {
        title: 'Versão completa para a banca',
        durationDays: 30,
        requirements: [{ kind: 'FILES', label: 'Tese completa (PDF)', minCount: 1, extensions: PDF }],
      },
      {
        title: 'Artigo derivado submetido',
        description: 'Requisito para a defesa: artigo com resultados da tese submetido a periódico ou conferência.',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Artigo submetido (PDF)', minCount: 1, extensions: PDF },
          { kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF },
        ],
      },
    ],
  },
  {
    id: 'plataforma-tese',
    name: 'Tese de doutorado (em artigos)',
    description:
      'A tese como conjunto de artigos que sustentam uma tese central, com a lógica da redação científica (Volpato) em cada artigo. Qualificação e defesa entram automaticamente.',
    modalityKeys: ['TESE'],
    steps: [
      {
        title: 'Tese central e perguntas dos estudos',
        description: 'Qual afirmação original a tese defende? Quais estudos (artigos) são necessários para sustentá-la?',
        durationDays: 30,
        requirements: [
          { kind: 'TEXT', label: 'Tese central (a afirmação a ser defendida)' },
          { kind: 'TEXT', label: 'Estudos/artigos previstos e a pergunta de cada um' },
          { kind: 'TEXT', label: 'Contribuição original em relação à literatura' },
        ],
      },
      {
        title: 'Estado da arte (revisão — artigo 1)',
        description: 'Revisão sistemática ou integrativa que fundamenta a lacuna; pode virar o primeiro artigo.',
        durationDays: 90,
        requirements: [
          { kind: 'FILES', label: 'Artigos incluídos (PDF)', minCount: 30, extensions: PDF },
          { kind: 'FILES', label: 'Manuscrito da revisão', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Projeto e métodos dos estudos',
        durationDays: 45,
        requirements: [
          { kind: 'FILES', label: 'Projeto de tese', minCount: 1, extensions: DOC },
          { kind: 'TEXT', label: 'Comitê de ética: situação', required: false },
        ],
      },
      {
        title: 'Estudo 2: coleta e análise',
        durationDays: 180,
        requirements: [
          { kind: 'FILES', label: 'Dados do estudo', minCount: 1, extensions: [...SHEET, 'zip', 'json', 'txt'] },
          { kind: 'FILES', label: 'Figuras e tabelas dos resultados', minCount: 1, extensions: [...DOC, 'png', 'jpg', 'jpeg', 'xlsx'] },
        ],
      },
      {
        title: 'Artigo 2: redação e submissão',
        description: 'Conclusão e título primeiro; depois discussão, método, introdução e resumo.',
        durationDays: 60,
        requirements: [
          { kind: 'TEXT', label: 'Conclusão e título do artigo' },
          { kind: 'FILES', label: 'Manuscrito submetido', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF },
        ],
      },
      {
        title: 'Estudo 3: coleta e análise',
        durationDays: 180,
        requirements: [
          { kind: 'FILES', label: 'Dados do estudo', minCount: 1, extensions: [...SHEET, 'zip', 'json', 'txt'] },
          { kind: 'FILES', label: 'Figuras e tabelas dos resultados', minCount: 1, extensions: [...DOC, 'png', 'jpg', 'jpeg', 'xlsx'] },
        ],
      },
      {
        title: 'Artigo 3: redação e submissão',
        durationDays: 60,
        requirements: [
          { kind: 'TEXT', label: 'Conclusão e título do artigo' },
          { kind: 'FILES', label: 'Manuscrito submetido', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF },
        ],
      },
      {
        title: 'Introdução geral e conclusão da tese',
        description: 'Integre os artigos: a introdução geral apresenta a tese central; a conclusão geral mostra como os estudos a sustentam.',
        durationDays: 45,
        requirements: [
          { kind: 'FILES', label: 'Introdução geral', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Conclusão geral', minCount: 1, extensions: DOC },
        ],
      },
      {
        title: 'Versão completa para a banca',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Tese completa (PDF)', minCount: 1, extensions: PDF },
          { kind: 'TEXT', label: 'Resumo' },
        ],
      },
    ],
  },
  {
    id: 'plataforma-pos-doutorado',
    name: 'Pós-doutorado: plano de trabalho',
    description: 'Plano, produção de artigos e relatórios parcial e final.',
    modalityKeys: ['POS_DOUTORADO'],
    steps: [
      {
        title: 'Plano de trabalho',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Plano de trabalho', minCount: 1, extensions: DOC },
          { kind: 'TEXT', label: 'Produtos esperados (artigos, orientações, disciplinas)' },
        ],
      },
      {
        title: 'Primeiro artigo submetido',
        durationDays: 150,
        requirements: [
          { kind: 'FILES', label: 'Manuscrito submetido', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF },
        ],
      },
      { title: 'Relatório parcial', durationDays: 30, requirements: [{ kind: 'FILES', label: 'Relatório parcial', minCount: 1, extensions: DOC }] },
      {
        title: 'Segundo artigo submetido',
        durationDays: 150,
        requirements: [
          { kind: 'FILES', label: 'Manuscrito submetido', minCount: 1, extensions: DOC },
          { kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF },
        ],
      },
      {
        title: 'Relatório final',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Relatório final', minCount: 1, extensions: DOC },
          { kind: 'NUMBER', label: 'Artigos submetidos no período', unit: 'artigos' },
        ],
      },
    ],
  },
  // ---------------------------------------------------------------- atividades simples de pesquisa
  {
    id: 'plataforma-fichamento',
    name: 'Leituras e fichamentos',
    description: 'Atividade curta: ler um conjunto de textos sobre o tema e entregar os fichamentos.',
    modalityKeys: [...ALL_RESEARCH],
    steps: [
      {
        title: 'Seleção das leituras',
        durationDays: 7,
        requirements: [
          { kind: 'FILES', label: 'Textos selecionados (PDF)', minCount: 5, extensions: PDF },
          { kind: 'TEXT', label: 'Critério de escolha (tema, palavras-chave, período)' },
        ],
      },
      {
        title: 'Fichamentos',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'Fichamentos (um por texto ou um arquivo único)', minCount: 1, extensions: DOC },
          { kind: 'NUMBER', label: 'Textos fichados', unit: 'textos', target: 5 },
        ],
      },
    ],
  },
  {
    id: 'plataforma-coleta-dados',
    name: 'Coleta de dados',
    description: 'Instrumento, teste piloto e coleta, com os dados organizados para análise.',
    modalityKeys: [...ALL_RESEARCH],
    steps: [
      {
        title: 'Instrumento de coleta',
        description: 'Questionário, roteiro de entrevista, protocolo de observação ou de experimento.',
        durationDays: 14,
        requirements: [{ kind: 'FILES', label: 'Instrumento de coleta', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Teste piloto',
        durationDays: 14,
        requirements: [
          { kind: 'TEXT', label: 'O que o piloto mostrou e os ajustes feitos' },
          { kind: 'FILES', label: 'Instrumento revisado', minCount: 1, extensions: DOC, required: false },
        ],
      },
      {
        title: 'Coleta',
        durationDays: 45,
        requirements: [
          { kind: 'FILES', label: 'Dados coletados (planilha, base ou transcrições)', minCount: 1, extensions: [...SHEET, 'zip', 'txt', 'docx'] },
          { kind: 'NUMBER', label: 'Participantes ou casos obtidos', unit: 'casos' },
        ],
      },
      {
        title: 'Organização dos dados',
        durationDays: 7,
        requirements: [
          { kind: 'FILES', label: 'Base de dados limpa', minCount: 1, extensions: [...SHEET, 'zip'] },
          { kind: 'FILES', label: 'Dicionário de variáveis / codificação', minCount: 1, extensions: [...DOC, ...SHEET], required: false },
        ],
      },
    ],
  },
  {
    id: 'plataforma-analise-dados',
    name: 'Análise de dados',
    description: 'Da base limpa às figuras e tabelas, com a interpretação dos resultados.',
    modalityKeys: [...ALL_RESEARCH],
    steps: [
      {
        title: 'Plano de análise',
        durationDays: 7,
        requirements: [{ kind: 'TEXT', label: 'Perguntas, variáveis e técnicas de análise' }],
      },
      {
        title: 'Análises',
        durationDays: 21,
        requirements: [{ kind: 'FILES', label: 'Scripts, saídas ou relatório da análise', minCount: 1, extensions: ['py', 'r', 'ipynb', 'pdf', 'docx', 'xlsx', 'zip'] }],
      },
      {
        title: 'Figuras, tabelas e interpretação',
        durationDays: 14,
        requirements: [
          { kind: 'FILES', label: 'Figuras e tabelas com legendas', minCount: 1, extensions: [...DOC, 'png', 'jpg', 'jpeg', 'xlsx'] },
          { kind: 'TEXT', label: 'Principais resultados (uma frase por figura/tabela)' },
        ],
      },
    ],
  },
  {
    id: 'plataforma-resumo-evento',
    name: 'Resumo para evento científico',
    description: 'Escolha do evento, resumo dentro das normas, submissão e apresentação (pôster ou oral).',
    modalityKeys: [...ALL_RESEARCH],
    steps: [
      {
        title: 'Escolha do evento',
        durationDays: 7,
        requirements: [
          { kind: 'TEXT', label: 'Evento, data e prazo de submissão' },
          { kind: 'LINK', label: 'Normas do evento' },
        ],
      },
      {
        title: 'Resumo',
        durationDays: 14,
        requirements: [{ kind: 'FILES', label: 'Resumo conforme as normas', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Submissão',
        durationDays: 7,
        requirements: [{ kind: 'FILES', label: 'Comprovante de submissão', minCount: 1, extensions: IMG_PDF }],
      },
      {
        title: 'Apresentação',
        durationDays: 30,
        requirements: [
          { kind: 'FILES', label: 'Pôster ou slides', minCount: 1, extensions: ['pdf', 'pptx'] },
          { kind: 'FILES', label: 'Certificado de apresentação', minCount: 1, extensions: IMG_PDF, required: false },
        ],
      },
    ],
  },
  {
    id: 'plataforma-relatorio-pesquisa',
    name: 'Relatório de pesquisa',
    description: 'Relatório parcial ou final (bolsa, agência, programa), com o que foi feito, resultados e próximos passos.',
    modalityKeys: [...ALL_RESEARCH],
    steps: [
      {
        title: 'Atividades e resultados do período',
        durationDays: 14,
        requirements: [
          { kind: 'TEXT', label: 'Atividades realizadas' },
          { kind: 'TEXT', label: 'Resultados e produtos (artigos, apresentações, dados)' },
        ],
      },
      {
        title: 'Relatório escrito',
        durationDays: 14,
        requirements: [{ kind: 'FILES', label: 'Relatório', minCount: 1, extensions: DOC }],
      },
      {
        title: 'Envio à agência/programa',
        durationDays: 7,
        requirements: [{ kind: 'FILES', label: 'Comprovante de envio', minCount: 1, extensions: IMG_PDF, required: false }],
      },
    ],
  },
  {
    id: 'plataforma-seminario',
    name: 'Seminário ou apresentação',
    description: 'Preparar e apresentar um seminário (grupo de pesquisa, disciplina, banca prévia).',
    modalityKeys: [...ALL_RESEARCH],
    steps: [
      {
        title: 'Roteiro da apresentação',
        durationDays: 7,
        requirements: [{ kind: 'TEXT', label: 'Mensagem principal e tópicos' }],
      },
      {
        title: 'Slides e ensaio',
        durationDays: 7,
        requirements: [{ kind: 'FILES', label: 'Slides', minCount: 1, extensions: ['pdf', 'pptx'] }],
      },
      {
        title: 'Apresentação e retorno',
        durationDays: 7,
        requirements: [{ kind: 'TEXT', label: 'Principais perguntas e sugestões recebidas' }],
      },
    ],
  },
];

/** Versões padrão dos modelos da plataforma (sincronizadas com o banco na subida da API). */
export const PLATFORM_STEP_TEMPLATES: PlatformStepTemplate[] = PLATFORM.map(({ id, ...t }) => ({ seedKey: id, ...stepTemplateInputSchema.parse(t) }));

export const templateTotalDays = (steps: Pick<TemplateStep, 'durationDays'>[]) => steps.reduce((sum, s) => sum + s.durationDays, 0);

/**
 * Prazos de cada etapa a partir da data de início. Sem `endDate`, as durações se encadeiam (cada etapa vence
 * `durationDays` após a anterior). Com `endDate` (ex.: prazo do título, no modelo escolhido no convite), as
 * durações são distribuídas proporcionalmente para que a última etapa vença nessa data — o mesmo modelo serve
 * a um curso de 24 ou 48 meses.
 */
export function scheduleTemplateSteps(steps: Pick<TemplateStep, 'durationDays'>[], startDate: IsoDate, endDate?: IsoDate): IsoDate[] {
  const total = templateTotalDays(steps);
  const span = endDate ? Math.max(1, diffDays(endDate, startDate)) : total;
  let elapsed = 0;
  return steps.map((s) => {
    elapsed += s.durationDays;
    return addDays(startDate, Math.max(1, Math.round((elapsed / total) * span)));
  });
}

/** Modelo pré-selecionado no convite pelo nível (os demais níveis começam sem modelo). */
export const PREFERRED_TEMPLATE_BY_LEVEL: Partial<Record<AcademicLevel, string>> = {
  INICIACAO_CIENTIFICA: IC_BASIC_TEMPLATE_SEED_KEY,
  MESTRADO: BASIC_TEMPLATE_SEED_KEY,
  DOUTORADO: BASIC_TEMPLATE_SEED_KEY,
};

/** Modelo sugerido por tipo de atividade (pré-selecionado no cadastro; a orientação pode trocar ou dispensar). */
export const PREFERRED_TEMPLATE_BY_MODALITY: Record<string, string> = {
  DISSERTACAO: 'plataforma-dissertacao',
  TESE: 'plataforma-tese-tradicional',
  ARTIGO: 'plataforma-artigo',
  ARTIGO_CONFERENCIA: 'plataforma-artigo',
  POS_DOUTORADO: 'plataforma-pos-doutorado',
  TCC: 'plataforma-tcc',
  MONOGRAFIA: 'plataforma-tcc',
  PESQUISA: 'plataforma-projeto-pesquisa',
};
